import React, { useContext, useEffect, useState } from 'react';
import tw from 'twin.macro';
import { Link, useLocation } from 'react-router-dom';
import { Container } from '../container';
import {
  DumpertLogoIcon,
  DumpertRainbowLogoIcon,
  MenuRightIcon,
  UploadIcon,
  UserIcon,
} from 'icons';
import { DropDown, DropDownItem } from '../dropdown';
import { AppContext, PreferenceContext, UserContext } from '../context';
import { Button } from '../button';
import { MainMenu, UploadMenu } from '../menus';
import { SearchForm } from '../form';
import { MobileSearchHeader } from '../search';
import { gaEvent } from 'services/ga';
import { TextLine } from '../text';
import { Icon } from '../icon';
import { CloseModalButton } from '../item';
import { userLogout } from 'services/user';
import { mainNavList } from '../../data/';
import { Marker } from '../marker';
import { decodeURIComponentSafely } from '../../lib/helpers';

export const HomeButton = ({ rainbow, ...restProps }) => {
  const { windowSize } = useContext(AppContext);
  const IconType = rainbow ? DumpertRainbowLogoIcon : DumpertLogoIcon;
  return (
    <Link to='/' tw='text-brand' alt='logo' {...restProps} aria-label='terug naar de voorpagina'>
      <IconType
        css={[
          tw`fill-current`,
          ['MOBILE'].includes(windowSize.adBreakPoint) && tw`w-[80px]`,
          !['MOBILE'].includes(windowSize.adBreakPoint) && tw`w-[150px]`,
        ]}
      />
    </Link>
  );
};

export const UnderlinedNav = ({
  itemsList = [],
  navLocation = false,
  selectedItem,
  ...restProps
}) => {
  const { windowSize } = useContext(AppContext);
  const { pathname, search } = useLocation();
  const activeItem = itemsList.findIndex((item) =>
    selectedItem ? item.upDatePreference === selectedItem : item.pathname === pathname,
  );

  const [selectedList, setSelectedList] = useState(itemsList[activeItem]);
  const isFooter = navLocation === 'footer';
  const isItemDetail = navLocation === 'itemDetail';
  const footerProps = isFooter ? { isFooter: true } : {};

  const { setPreference } = useContext(PreferenceContext);

  useEffect(() => {
    setSelectedList(itemsList[activeItem]);
  }, [pathname, search]);

  return (
    <nav css={[(isFooter || isItemDetail) && tw`w-full flex`]}>
      {/* TODO (MINOR): Warning: React does not recognize the `hasTopBar` prop on a DOM element. */}
      {itemsList.map((item, i) => (
        <item.ElementType
          key={i}
          to={
            item.pathnameMobile && ['MOBILE'].includes(windowSize.adBreakPoint)
              ? item.pathnameMobile
              : item.pathname
          }
          onClick={() => {
            setSelectedList(item);
            if (item.upDatePreference) {
              setPreference({ detailsPageMobileBlock: item.upDatePreference });
            }
          }}
          className='group text-light'
          css={[
            tw`relative cursor-pointer`,
            (isFooter || isItemDetail) && tw`flex flex-grow justify-center items-center p-0 pt-2`,
            !item.IconType && tw`pt-4`,
          ]}
          {...restProps}
          {...footerProps}
        >
          {isFooter && item.IconType && <Icon Type={item.IconType} size='medium' tw='mx-auto' />}
          <TextLine black>{item.label}</TextLine>
          {item.label === 'Statistieken' && (
            <Marker
              label='NIEUW'
              css={[
                tw`absolute top-[-7px] left-[7px]`,
                isFooter && tw`bottom-[-20px] top-auto left-auto`,
              ]}
            />
          )}
          {item === selectedList && (
            <div
              css={[
                tw`w-full h-[3px] absolute left-0 right-0 bg-transparent`,
                !isFooter && tw`-bottom-1`,
                isFooter && tw`-top-1`,
                isItemDetail && tw`bg-brand`,
              ]}
            ></div>
          )}
        </item.ElementType>
      ))}
    </nav>
  );
};

const homeButtonClickGAEvent = () => gaEvent('dmp_actions', 'logo_click');

export const Header = ({ children, isModal, ...restProps }) => {
  const { slideIn, setSlideIn, windowSize } = useContext(AppContext);
  const location = useLocation();
  const { pathname, search } = location;
  const { user } = useContext(UserContext);
  const decodeNextLoginUrl = decodeURIComponentSafely(pathname + search).replace(
    '/inloggen?next=',
    '',
  );
  const loginUrl =
    (pathname.includes('inloggen') && search === '') || (pathname === '/' && search === '')
      ? '/inloggen'
      : `/inloggen?next=${encodeURIComponent(decodeNextLoginUrl)}`;

  return (
    <header tw='sticky top-0 z-40 h-[58px] md:h-auto bg-header' {...restProps}>
      <div css={[tw`relative`, { overflowX: 'hidden' }, slideIn.active && tw`overflow-x-visible`]}>
        {children}
      </div>
      <Container tw='relative flex justify-between items-center h-14'>
        <HomeButton onClick={homeButtonClickGAEvent} />
        <div tw='flex gap-2 items-center ml-2'>
          {['DESKTOP'].includes(windowSize.adBreakPoint) && (
            <div tw='flex-shrink-0'>
              <UnderlinedNav
                itemsList={mainNavList(user)}
                navLocation='header-pos'
                selectedItem=''
              />
            </div>
          )}
          {['MOBILE'].includes(windowSize.adBreakPoint) && <MobileSearchHeader />}
          {!['MOBILE'].includes(windowSize.adBreakPoint) && <SearchForm />}
          <Button
            primary
            label='insturen'
            iconProps={{ Type: UploadIcon, color: 'dark', size: 'medium' }}
            animated
            tw='z-10'
            onClick={() => {
              gaEvent('dmp_upload', 'open');
              setSlideIn({
                fullwidth: false,
                active: true,
                Component: UploadMenu,
              });
            }}
          />
          <DropDown
            isRight
            label={user ? 'ingelogd' : 'Inloggen'}
            hideLabel={['MOBILE'].includes(windowSize.adBreakPoint)}
            iconProps={{ Type: UserIcon, color: user ? 'brand' : 'icons' }}
            buttonProps={{ secondary: true }}
          >
            {!user && (
              <>
                <DropDownItem>
                  <Link to={loginUrl}>Inloggen</Link>
                </DropDownItem>
                <DropDownItem>
                  <Link to='/registreren'>Registreren</Link>
                </DropDownItem>
              </>
            )}
            {user && (
              <>
                <DropDownItem tw='cursor-default'>
                  <span tw='text-brand font-black uppercase'>{user.name}</span>
                </DropDownItem>
                <DropDownItem
                  onClick={() => {
                    setSlideIn({
                      fullwidth: false,
                      active: false,
                      Component: MainMenu,
                    });
                  }}
                >
                  <Link to='/reaguurder'>Reaguurder</Link>
                </DropDownItem>
                <DropDownItem
                  onClick={() => {
                    setSlideIn({
                      fullwidth: false,
                      active: false,
                      Component: MainMenu,
                    });
                  }}
                >
                  <Link to='/reaguurder/instellingen'>Reaguurderinstellingen</Link>
                </DropDownItem>
                <DropDownItem
                  onClick={() => {
                    setSlideIn({
                      fullwidth: false,
                      active: false,
                      Component: MainMenu,
                    });
                  }}
                >
                  <Link to={`/profiel/${encodeURI(user.name)}`} tw='cursor-pointer'>
                    Mijn Profiel
                  </Link>
                </DropDownItem>
                <DropDownItem
                  onClick={() => {
                    setSlideIn({
                      fullwidth: false,
                      active: false,
                      Component: MainMenu,
                    });
                  }}
                >
                  <Link to={'/reaguurder/badges'} tw='cursor-pointer'>
                    Mijn badges
                  </Link>
                </DropDownItem>
                <DropDownItem
                  onClick={() => {
                    setSlideIn({
                      fullwidth: false,
                      active: false,
                      Component: MainMenu,
                    });
                  }}
                >
                  <Link to={`/reaguurder/afspeellijsten/${user.dumpertId}`} tw='cursor-pointer'>
                    Mijn afspeellijsten
                  </Link>
                </DropDownItem>
                <hr tw='border-input-back' />
                <DropDownItem
                  label='Uitloggen'
                  onClick={() => {
                    userLogout();
                  }}
                />
              </>
            )}
          </DropDown>
          <Button
            label='menu'
            hideLabel={['MOBILE'].includes(windowSize.adBreakPoint)}
            iconProps={{
              Type: MenuRightIcon,
            }}
            className='group text-light'
            onClick={() => {
              gaEvent('dmp_action', 'menu_open');
              setSlideIn({
                fullwidth: false,
                active: true,
                Component: MainMenu,
              });
            }}
          />
          {isModal && ['DESKTOP'].includes(windowSize.adBreakPoint) && <CloseModalButton topBar />}
        </div>
      </Container>
    </header>
  );
};
